import { Info, KeyRound, LayoutGrid, Settings2, ShieldCheck, Sparkles, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { Role } from '@shared/types'
import { IconButton } from '@/components/ui'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/useAuthStore'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Match this path exactly instead of also matching its children. */
  end?: boolean
  adminOnly?: boolean
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/sites', label: 'Sites', icon: LayoutGrid },
  // Admin-only: one WP Engine key is designated for the whole instance, so a
  // regular user has nothing to manage here and every site they connect uses it
  // automatically. See services/ssh-key-access.ts.
  { to: '/keys', label: 'SSH keys', icon: KeyRound, adminOnly: true },
  { to: '/settings', label: 'Settings', icon: Settings2 }
]

const SECONDARY_NAV: NavItem[] = [
  { to: '/admin', label: 'Admin', icon: ShieldCheck, adminOnly: true },
  { to: '/about', label: 'About', icon: Info }
]

interface SidebarProps {
  /** Called after any nav activation so the mobile drawer can close itself. */
  onNavigate?: () => void
  /** Provided only in the drawer, where the panel needs its own close affordance. */
  onClose?: () => void
}

interface NavRowProps {
  item: NavItem
  onNavigate?: () => void
}

function NavRow({ item, onNavigate }: NavRowProps): React.ReactElement {
  const { to, label, icon: Icon, end } = item

  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-control px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-accent-soft text-accent'
            : 'text-fg-muted hover:bg-sunken hover:text-fg'
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn('h-4 w-4 shrink-0', isActive ? 'text-accent' : 'text-fg-subtle')}
            aria-hidden="true"
          />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  )
}

export default function Sidebar({ onNavigate, onClose }: SidebarProps): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const isAdmin = user?.role === Role.Admin

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface">
      <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-line px-4">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[0.6rem] bg-accent text-accent-fg shadow-sheet">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] leading-tight font-semibold tracking-tight text-fg">
            Argus
          </span>
          <span className="block truncate text-[11px] leading-tight text-fg-subtle">
            WordPress alt text manager
          </span>
        </span>
        {onClose && (
          <IconButton label="Close navigation" size="sm" onClick={onClose} className="lg:hidden">
            <X className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        )}
      </div>

      <nav aria-label="Main" className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {PRIMARY_NAV.filter((item) => !item.adminOnly || isAdmin).map((item) => (
            <li key={item.to}>
              <NavRow item={item} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>

        <hr className="my-4 border-t border-line" />

        <ul className="space-y-1">
          {SECONDARY_NAV.filter((item) => !item.adminOnly || isAdmin).map((item) => (
            <li key={item.to}>
              <NavRow item={item} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      </nav>

      <div className="shrink-0 border-t border-line px-4 py-3.5 text-center">
        <p className="text-[11px] leading-relaxed text-fg-subtle">
          Made by{' '}
          <a
            href="https://9cloudwebworks.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-accent underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current"
          >
            Wasim Sayyed
          </a>
        </p>
      </div>
    </div>
  )
}

export { Sidebar }
